import React from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaMapMarkerAlt, FaCalendarAlt, FaClock, FaStar, FaCheckCircle, FaTimesCircle, FaHotel } from "react-icons/fa";
import "../css/styles.css";

const KenyaHighlights = () => {
  return (
    <div className="safari-detail-page">
      {/* ===== HERO ===== */}
      <section className="safari-detail-hero">
        <div className="safari-detail-hero-bg">
          <img
            src="https://res.cloudinary.com/dn39bkkiq/image/upload/q_auto,f_auto,w_1400/v1776348690/7_Day_Highlights_of_kenya_safari_2_zynbfv.jpg"
            alt="Kenya Highlights Safari"
          />
          <div className="safari-detail-hero-overlay" />
        </div>
        <div className="safari-detail-hero-content">
          <span className="safari-detail-eyebrow">Kenya Safari</span>
          <h1>7 Day Highlights of Kenya Safari</h1>
          <p className="safari-detail-hero-sub">
            From the legendary Masai Mara to the elephants of Amboseli beneath Mount Kilimanjaro — the best of Kenya in one journey.
          </p>
          <div className="safari-detail-hero-buttons">
            <Link to="/contact" className="hero-cta primary">
              Plan Your Safari <FaArrowRight className="btn-icon" />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== OVERVIEW ===== */}
      <section className="safari-detail-overview">
        <div className="safari-detail-section-header">
          <span className="section-tag">Safari Overview</span>
          <h2>The Best of Kenya in Seven Days</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-text">
          <p>
            This safari brings together some of the most beautiful and wildlife-rich places in Kenya. Your journey begins in the legendary Masai Mara, where endless golden plains are home to lions, elephants, giraffes, and vast herds of wildlife. It is one of Africa's most famous safari destinations and offers some of the best wildlife viewing in the world.
          </p>
          <p>
            The adventure continues to Lake Nakuru National Park, a scenic park in the Great Rift Valley known for its rhinos, buffalo, and beautiful lake surrounded by cliffs and forests.
          </p>
          <p>
            The final destination is Amboseli National Park, famous for its large elephant herds and breathtaking views of Mount Kilimanjaro rising above the plains.
          </p>
          <p>
            With diverse landscapes, rich wildlife, and unforgettable scenery, this safari offers a wonderful introduction to Kenya's natural beauty.
          </p>
        </div>
      </section>

      {/* ===== HIGHLIGHTS ===== */}
      <section className="safari-detail-highlights">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Highlights</span>
          <h2>Safari Highlights</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-highlights-list">
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Explore the vast wildlife plains of the Masai Mara</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Search for lions, elephants, and other members of the Big Five</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Visit Lake Nakuru National Park, home to rhinos and beautiful landscapes</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Experience Amboseli National Park, famous for its elephants and views of Mount Kilimanjaro</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Scenic drives through the Great Rift Valley</p>
          </div>
        </div>
      </section>

      {/* ===== AT A GLANCE ===== */}
      <section className="safari-detail-glance">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Quick Info</span>
          <h2>Safari at a Glance</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-glance-grid">
          <div className="safari-detail-glance-card">
            <FaClock className="safari-detail-glance-icon" />
            <h4>Duration</h4>
            <p>7 Days / 6 Nights</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaMapMarkerAlt className="safari-detail-glance-icon" />
            <h4>Start / End</h4>
            <p>Nairobi</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaMapMarkerAlt className="safari-detail-glance-icon" />
            <h4>Destinations</h4>
            <p>Masai Mara &ndash; Lake Nakuru &ndash; Amboseli</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaCalendarAlt className="safari-detail-glance-icon" />
            <h4>Best Time to Travel</h4>
            <p>All year round</p>
          </div>
        </div>
      </section>

      {/* ===== ITINERARY ===== */}
      <section className="safari-detail-itinerary">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Day by Day</span>
          <h2>Day-by-Day Itinerary</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-days">
          {/* Day 1 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">1</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 1 — Arrival in Nairobi</h3>
              <p>
                Welcome to Kenya. Upon arrival at Jomo Kenyatta International Airport, you will be warmly welcomed by a Nyatwende representative and transferred to your hotel.
              </p>
              <p>
                Depending on your arrival time, you may relax after your journey or explore some of Nairobi's attractions such as the Giraffe Centre or the David Sheldrick Elephant Orphanage.
              </p>
              <p className="safari-detail-day-note">Overnight in Nairobi.</p>
            </div>
          </div>

          {/* Day 2 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">2</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 2 — Nairobi to Masai Mara</h3>
              <p>
                After breakfast, depart Nairobi and begin your journey towards the famous Masai Mara National Reserve. Along the way, stop at a viewpoint overlooking the dramatic Great Rift Valley, one of Africa's most impressive landscapes.
              </p>
              <p>
                Continue the drive through rural towns and rolling hills before arriving in the Masai Mara in time for lunch at your lodge or camp.
              </p>
              <p>
                Later in the afternoon, head out for your first exciting game drive in search of wildlife across the vast plains.
              </p>
              <p className="safari-detail-day-note">Overnight in the Masai Mara.</p>
            </div>
          </div>

          {/* Day 3 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">3</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 3 — Full Day in the Masai Mara</h3>
              <p>
                Today is a full day to explore the incredible wildlife of the Masai Mara. Morning and afternoon game drives take you across the reserve where lions, elephants, giraffes, buffalo, and many other animals roam freely.
              </p>
              <p>
                The Mara is also famous for the Great Wildebeest Migration, when millions of wildebeest and zebras move across the plains in search of fresh grass.
              </p>
              <p>
                You may also choose to visit a Maasai cultural village to learn about the traditions of the Maasai people, or experience an unforgettable sunrise hot air balloon safari above the savannah.
              </p>
              <p className="safari-detail-day-note">Overnight in the Masai Mara.</p>
            </div>
          </div>

          {/* Day 4 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">4</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 4 — Masai Mara to Lake Nakuru</h3>
              <p>
                After breakfast, depart the Masai Mara and travel towards the Great Rift Valley to Lake Nakuru National Park. Arrive in time for lunch followed by an afternoon game drive.
              </p>
              <p>
                Lake Nakuru is known for its rhinos and is one of the best places in Kenya to spot both black and white rhinos. The park is also home to lions, buffalo, giraffes, zebras, and a wide variety of birdlife.
              </p>
              <p>
                The lake itself, surrounded by forests and cliffs, makes this park especially scenic.
              </p>
              <p className="safari-detail-day-note">Overnight in Lake Nakuru.</p>
            </div>
          </div>

          {/* Day 5 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">5</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 5 — Lake Nakuru to Amboseli</h3>
              <p>
                After breakfast, continue your journey south towards Amboseli National Park. The drive takes you through changing landscapes as you approach the open plains near the border of Kenya and Tanzania.
              </p>
              <p>
                Arrive in time to relax at your lodge while enjoying views of Mount Kilimanjaro, Africa's highest mountain.
              </p>
              <p className="safari-detail-day-note">Overnight in Amboseli.</p>
            </div>
          </div>

          {/* Day 6 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">6</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 6 — Full Day in Amboseli</h3>
              <p>
                Today is dedicated to exploring Amboseli National Park, one of Kenya's most iconic safari destinations. Amboseli is famous for its large elephant herds that roam the plains with the snow-capped peak of Mount Kilimanjaro rising in the background.
              </p>
              <p>
                Morning and afternoon game drives offer opportunities to see elephants, lions, buffalo, giraffes, zebras, and many other animals.
              </p>
              <p>
                The open landscapes and dramatic views make Amboseli a favorite for photographers and wildlife lovers.
              </p>
              <p className="safari-detail-day-note">Overnight in Amboseli.</p>
            </div>
          </div>

          {/* Day 7 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">7</div>
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 7 — Amboseli to Nairobi</h3>
              <p>
                After breakfast, enjoy the peaceful morning views of the park before beginning the journey back to Nairobi.
              </p>
              <p>
                You will arrive in the afternoon where you will be dropped off at your hotel or transferred to the airport for your onward journey.
              </p>
              <p className="safari-detail-day-note">
                Your safari ends here, leaving you with unforgettable memories of Kenya's landscapes and wildlife.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== ACCOMMODATION ===== */}
      <section className="safari-detail-accommodation">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Where You'll Stay</span>
          <h2>Accommodation Examples</h2>
          <div className="safari-detail-line" />
        </div>

        {/* Nairobi */}
        <h3 className="safari-detail-accom-region-title">Nairobi</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">City Hotels</div>
            <ul>
              <li><FaHotel /> Tamarind Tree Hotel</li>
              <li><FaHotel /> Four Points by Sheraton Nairobi Airport</li>
            </ul>
          </div>
        </div>

        {/* Masai Mara */}
        <h3 className="safari-detail-accom-region-title">Masai Mara</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier budget">Budget</div>
            <ul>
              <li><FaHotel /> Enkorok Mara Camp</li>
            </ul>
          </div>
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">Midrange</div>
            <ul>
              <li><FaHotel /> Mara Sopa Lodge</li>
            </ul>
          </div>
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Luxury</div>
            <ul>
              <li><FaHotel /> Mara Serena Safari Lodge</li>
            </ul>
          </div>
        </div>

        {/* Lake Nakuru */}
        <h3 className="safari-detail-accom-region-title">Lake Nakuru</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">Midrange</div>
            <ul>
              <li><FaHotel /> Lake Nakuru Sopa Lodge</li>
              <li><FaHotel /> Sarova Lion Hill Game Lodge</li>
            </ul>
          </div>
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Luxury</div>
            <ul>
              <li><FaHotel /> The Cliff Nakuru</li>
            </ul>
          </div>
        </div>

        {/* Amboseli */}
        <h3 className="safari-detail-accom-region-title">Amboseli</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier budget">Budget</div>
            <ul>
              <li><FaHotel /> Sentrim Amboseli Lodge</li>
            </ul>
          </div>
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">Midrange</div>
            <ul>
              <li><FaHotel /> Amboseli Serena Safari Lodge</li>
            </ul>
          </div>
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Luxury</div>
            <ul>
              <li><FaHotel /> Ol Tukai Lodge</li>
            </ul>
          </div>
        </div>

        <p className="safari-detail-accom-note">
          Similar lodges of the same standard may be used depending on availability.
        </p>
      </section>

      {/* ===== INCLUDED / EXCLUDED ===== */}
      <section className="safari-detail-inclusions">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">What to Expect</span>
          <h2>What's Included</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-inclusions-grid">
          <div className="safari-detail-inclusions-card included">
            <h3><FaCheckCircle /> Included</h3>
            <ul>
              <li>Airport transfers in Nairobi</li>
              <li>Transport in a comfortable safari vehicle with a professional guide</li>
              <li>Park entrance fees</li>
              <li>Accommodation during the safari</li>
              <li>Meals as specified in the itinerary</li>
              <li>Bottled drinking water during game drives</li>
            </ul>
          </div>
          <div className="safari-detail-inclusions-card excluded">
            <h3><FaTimesCircle /> Not Included</h3>
            <ul>
              <li>International flights</li>
              <li>Travel insurance</li>
              <li>Visa fees</li>
              <li>Optional activities such as Maasai village visits or hot air balloon safari</li>
              <li>Personal expenses</li>
              <li>Tips and gratuities</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="safari-detail-cta">
        <div className="safari-detail-cta-content">
          <h2>Customize This Safari</h2>
          <p>
            This safari can be tailored to suit your travel style. You may upgrade lodges, extend your safari, or combine it with a relaxing beach holiday on the Kenyan coast.
          </p>
          <div className="safari-detail-cta-buttons">
            <Link to="/contact?safari=7 Day Highlights of Kenya Safari" className="hero-cta primary">
              Plan Your Safari <FaArrowRight className="btn-icon" />
            </Link>
            <Link to="/tours" className="hero-cta secondary">
              View More Safaris
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default KenyaHighlights;
